import {
  DUTY_ANALYTICS_DEFAULT_RANGE,
  type DashboardTileColumnDto,
  DUTY_ANALYTICS_METRICS,
  DUTY_ANALYTICS_RANGES,
  calendarDateSchema,
  dutyAnalyticsWindowRefusal,
  readableDate,
  type DutyAnalyticsAttendanceFiguresDto,
  type DutyAnalyticsDelegationFiguresDto,
  type DutyAnalyticsFillFiguresDto,
  type DutyAnalyticsIdentityDto,
  type DutyAnalyticsLayout,
  type DutyAnalyticsMetric,
  type DutyAnalyticsRange,
  type DutyAnalyticsWindowDto,
} from '@pams/shared';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [P7C §2] DUTY ANALYTICS — THE PURE HALF OF THE PAGE: WHERE A COUNT LEADS, AND HOW THREE SECTIONS
 *  BECOME ONE ROW OF SEVEN COLUMNS.
 *
 *  ⚠️ NO ARITHMETIC ON A FIGURE BUT THE BAR'S CLAMP AND ITS SUBTRACTIONS (§2d). Every number on the
 *  page is a count the server sent; the one comparison that needs a ratio — the best mapping card —
 *  was decided on the server by integer cross-multiplication, so nothing here divides anything.
 *  `noScore.test.ts` holds that for every file of the feature.
 *
 *  ⚠️ THE CELL MODEL IS THE SERVER'S. A drill names the same `(divisionId?, orgUnitId?)` the page cell
 *  was built from, so the drill's `figure` equals the cell by construction:
 *    · COMMISSIONERATE — the total: neither; a division row: `divisionId`; a unit row: both.
 *    · DIVISION — the total: neither; a unit row, the division's own kept row included: `orgUnitId`
 *      alone. A `divisionId` on this layout is a 404 by design (no existence oracle), so it is never
 *      sent — and the commissionerate totals beside it (U2) reach no drill at all.
 *
 *  ⚠️ [amended P7C.1 §4 · §5] TWO THINGS RIDE BESIDE THE CELL, AND NEITHER CHANGES IT.
 *    · `asDivisionId` — a division's page read AS that division from the commissionerate takes the
 *      DIVISION model above, and every request it makes carries the division. It never travels with a
 *      `divisionId`: the server answers that pair with its one 404, so no link here writes it.
 *    · the WINDOW — a preset (`range`, omitted at the default) or a custom pair (`dateFrom`/`dateTo`),
 *      written where the range would be and never both. `DutyAnalyticsWindowParam` is the one type every
 *      builder takes, so a preset call is byte-identical to what it was before the pair existed.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/** The API's paths. The web routes happen to be spelled the same; the drill link is the web route. */
export const DUTY_ANALYTICS_PATH = '/duty-analytics';
export const DUTY_ANALYTICS_ROWS_PATH = '/duty-analytics/rows';

/** Which of the three sections a column is read from. A section that failed blanks its own columns only. */
export type FigureGroup = 'fill' | 'delegation' | 'attendance';

type FigureKey =
  | keyof DutyAnalyticsFillFiguresDto
  | Exclude<keyof DutyAnalyticsDelegationFiguresDto, 'recipients'>
  | Exclude<keyof DutyAnalyticsAttendanceFiguresDto, 'markedOnBehalf' | 'markedSelf'>;

/**
 * ⚠️ THE TABLE'S SEVEN COLUMNS, IN THE BRIEF'S ORDER — "short-falls declared" BESIDE "shares left open",
 * because the two read alike and are opposite acts: one division has answered, the other has not.
 */
export const FIGURE_COLUMNS = [
  { key: 'asked', metric: 'ASKED', group: 'fill' },
  { key: 'named', metric: 'NAMED', group: 'fill' },
  { key: 'delegated', metric: 'DELEGATED', group: 'delegation' },
  { key: 'declaredShort', metric: 'DECLARED_SHORT', group: 'fill' },
  { key: 'leftOpen', metric: 'LEFT_OPEN', group: 'fill' },
  { key: 'reached', metric: 'REACHED', group: 'attendance' },
  { key: 'absent', metric: 'ABSENT', group: 'attendance' },
] as const satisfies readonly { key: FigureKey; metric: DutyAnalyticsMetric; group: FigureGroup }[];

export type FigureColumn = (typeof FIGURE_COLUMNS)[number];

/**
 * ⚠️ THE TWO MARKING COUNTS, NEVER ADDED (U1, [C1c §2d]). An officer's own tap and a marking entered on
 * their behalf are different facts about who said so, and a sum would erase the distinction the band
 * exists to show.
 */
export const MARKING_COLUMNS = [
  { key: 'markedSelf', metric: 'MARKED_SELF' },
  { key: 'markedOnBehalf', metric: 'MARKED_ON_BEHALF' },
] as const satisfies readonly { key: keyof DutyAnalyticsAttendanceFiguresDto; metric: DutyAnalyticsMetric }[];

/** One row's figures, by section. `null` is a section that FAILED — never zero, which would be a claim. */
export type GroupedFigures = {
  readonly fill: DutyAnalyticsFillFiguresDto | null;
  readonly delegation: DutyAnalyticsDelegationFiguresDto | null;
  readonly attendance: DutyAnalyticsAttendanceFiguresDto | null;
};

export type FigureRow = { readonly identity: DutyAnalyticsIdentityDto; readonly figures: GroupedFigures };

/** A column's value in one row, or `null` when its section could not be read. */
export const figureOf = (figures: GroupedFigures, column: FigureColumn): number | null => {
  switch (column.group) {
    case 'fill':
      return figures.fill === null ? null : figures.fill[column.key];
    case 'delegation':
      return figures.delegation === null ? null : figures.delegation[column.key];
    case 'attendance':
      return figures.attendance === null ? null : figures.attendance[column.key];
  }
};

export type Keyed = { readonly orgUnitId: string; readonly divisionId: string | null };

/** The join key every section's `rows`/`units` share with the identities (the contract's `keyed`). */
export const rowKeyOf = (row: Keyed): string => `${row.orgUnitId}|${row.divisionId ?? ''}`;

const ZERO_FILL: DutyAnalyticsFillFiguresDto = { asked: 0, named: 0, declaredShort: 0, leftOpen: 0 };
const ZERO_DELEGATION: DutyAnalyticsDelegationFiguresDto = { delegated: 0, recipients: 0 };
export const ZERO_ATTENDANCE: DutyAnalyticsAttendanceFiguresDto = {
  reached: 0,
  absent: 0,
  markedOnBehalf: 0,
  markedSelf: 0,
};

/**
 * One section's figures for one key. A section that has DATA but no row for the key means its fold
 * produced nothing there — zero, honestly; a section that FAILED is `null`.
 */
const pick = <F extends object>(rows: readonly (F & Keyed)[] | null, key: string, zero: F): F | null => {
  if (rows === null) return null;
  const found = rows.find((row) => rowKeyOf(row) === key);
  return found ?? zero;
};

/**
 * ⚠️ THE IDENTITIES DECIDE WHO APPEARS, IN THE SERVER'S ORDER, AND THE SECTIONS ONLY FILL COLUMNS. A
 * section that failed leaves its columns empty without hiding the row — the contract's own reason for
 * sending identities separately. No client sort: the order is the server's (asked, most first).
 */
export const joinFigureRows = (
  identities: readonly DutyAnalyticsIdentityDto[],
  sections: {
    readonly fill: readonly (DutyAnalyticsFillFiguresDto & Keyed)[] | null;
    readonly delegation: readonly (DutyAnalyticsDelegationFiguresDto & Keyed)[] | null;
    readonly attendance: readonly (DutyAnalyticsAttendanceFiguresDto & Keyed)[] | null;
  },
): FigureRow[] =>
  identities.map((identity) => {
    const key = rowKeyOf(identity);
    return {
      identity,
      figures: {
        fill: pick(sections.fill, key, ZERO_FILL),
        delegation: pick(sections.delegation, key, ZERO_DELEGATION),
        attendance: pick(sections.attendance, key, ZERO_ATTENDANCE),
      },
    };
  });

/** The groups a row cannot show, so the table can say so once rather than per cell. */
export const failedGroupsOf = (figures: GroupedFigures): FigureGroup[] =>
  (['fill', 'delegation', 'attendance'] as const).filter((group) => figures[group] === null);

// ─────────────────────────────────────────────────────────────────────────────────
//  WHERE A COUNT LEADS
// ─────────────────────────────────────────────────────────────────────────────────

export type DrillCell = { readonly divisionId?: string; readonly orgUnitId?: string };

/** Which list a row came from — the identities' `rows` or the commissionerate layout's `units`. */
export type RowList = 'rows' | 'units';

/**
 * The cell a row's counts drill to — the model in this file's header, and nothing else.
 *
 * ⚠️ ON THE DIVISION LAYOUT A `divisionId` IS NEVER SENT, even for the division's own kept row: the
 * server answers any `divisionId` there with a 404, and its body is the same for a real id and a
 * fabricated one, so the page cannot be used to ask whether another division exists.
 */
export const cellFor = (layout: DutyAnalyticsLayout, list: RowList, identity: Keyed): DrillCell => {
  if (layout === 'DIVISION') return { orgUnitId: identity.orgUnitId };
  if (list === 'rows') return { divisionId: identity.orgUnitId };
  return identity.divisionId === null
    ? { orgUnitId: identity.orgUnitId }
    : { divisionId: identity.divisionId, orgUnitId: identity.orgUnitId };
};

/** The view's own total — neither id, on either layout. */
export const TOTAL_CELL: DrillCell = {};

// ─────────────────────────────────────────────────────────────────────────────────
//  [P7C.1 §5] THE WINDOW A LINK CARRIES
// ─────────────────────────────────────────────────────────────────────────────────

/** A custom window, in the request's own naming — the dashboard's and the Audit log's `dateFrom`/`dateTo`. */
export type CustomWindow = { readonly dateFrom: string; readonly dateTo: string };

/**
 * ⚠️ [P7C.1 §5] A PRESET OR A CUSTOM PAIR, NEVER BOTH. Every builder below takes this where it took a range, so
 * a preset call is unchanged and a pair is written where the range would be.
 */
export type DutyAnalyticsWindowParam = DutyAnalyticsRange | CustomWindow;

/** The window the server ECHOED, as a builder takes it — the echo's `range` is `null` for a custom window. */
export const windowParamOf = (echo: DutyAnalyticsWindowDto): DutyAnalyticsWindowParam =>
  echo.range ?? { dateFrom: echo.from, dateTo: echo.to };

/**
 * A `YYYY-MM-DD` that exists, or `null`. `calendarDateSchema` is the API's own check, so `…-02-30` and a bare
 * `2026-10` fail here exactly as they 422 there — and `dutyAnalyticsWindowRefusal`'s precondition holds.
 */
export const calendarDateOf = (raw: string | null | undefined): string | null =>
  raw !== null && raw !== undefined && calendarDateSchema.safeParse(raw).success ? raw : null;

/**
 * ⚠️ A URL PAIR THE PAGE MAY SEND, OR `null`: both dates parse AND the shared rule accepts them against `today`.
 * The server re-asks the same rule with its own today, so a pair this returns is one it answers — and a pair it
 * refuses is never sent, which is why a hand-typed `?dateFrom=` the rule refuses falls back to the preset.
 */
export const customWindowOf = (
  dateFrom: string | null | undefined,
  dateTo: string | null | undefined,
  today: string,
): CustomWindow | null => {
  const from = calendarDateOf(dateFrom);
  const to = calendarDateOf(dateTo);
  if (from === null || to === null) return null;
  return dutyAnalyticsWindowRefusal(from, to, today) === null ? { dateFrom: from, dateTo: to } : null;
};

/** Writes a window where the range goes: a preset omitted at the default (`clearOnDefault`'s rule), a pair whole. */
const setWindow = (params: URLSearchParams, range: DutyAnalyticsWindowParam): void => {
  if (typeof range !== 'number') {
    params.set('dateFrom', range.dateFrom);
    params.set('dateTo', range.dateTo);
  } else if (range !== DUTY_ANALYTICS_DEFAULT_RANGE) params.set('range', String(range));
};

/**
 * `/duty-analytics/rows?metric=…&divisionId=…&orgUnitId=…&asDivisionId=…&range=…&division=…`, keys in that fixed
 * order so one cell is one URL. ⚠️ The range is omitted at the default — `clearOnDefault`'s rule, so a shared link
 * does not bake in whatever was the default the day it was copied. [amended P7C.1 §5] `range` is a preset or a
 * custom pair, written in its place.
 */
export const drillHref = (
  metric: DutyAnalyticsMetric,
  cell: DrillCell,
  range: DutyAnalyticsWindowParam,
  pageDivision?: string,
  asDivisionId?: string,
): string => {
  const params = new URLSearchParams({ metric });
  if (cell.divisionId !== undefined) params.set('divisionId', cell.divisionId);
  if (cell.orgUnitId !== undefined) params.set('orgUnitId', cell.orgUnitId);
  /*
   * ⚠️ [P7C.1 §4] THE DIVISION THIS PAGE IS READ AS — SENT TO THE SERVER, UNLIKE `division` BELOW. Without it a
   * count on a division's page opened from the commissionerate would drill the caller's OWN view: Central's total
   * would open "All divisions", and a unit cell would 422.
   */
  if (asDivisionId !== undefined && asDivisionId !== '') params.set('asDivisionId', asDivisionId);
  setWindow(params, range);
  /*
   * ⚠️ [P7C §2] THE PAGE'S OWN NARROWING, CARRIED FOR THE WAY BACK AND NEVER SENT TO THE SERVER. The
   * parent link used to narrow by the drilled cell's `divisionId`, so opening a DIVISION row's count
   * and going back narrowed the Units table though the operator never had. `division` is the page's
   * `?division=` verbatim — absent when the page was not narrowed.
   */
  if (pageDivision !== undefined && pageDivision !== '') params.set('division', pageDivision);
  return `${DUTY_ANALYTICS_ROWS_PATH}?${params.toString()}`;
};

/** [P7C.1 §4] The web route of a division's own page, read from the commissionerate. */
export const DUTY_ANALYTICS_DIVISIONS_PATH = '/duty-analytics/divisions';

const pageHref = (path: string, range: DutyAnalyticsWindowParam, division?: string): string => {
  const params = new URLSearchParams();
  setWindow(params, range);
  if (division !== undefined && division !== '') params.set('division', division);
  const query = params.toString();
  return query === '' ? path : `${path}?${query}`;
};

/** Back to the page — the drill's parent link — keeping the window and, when there was one, the narrowing. */
export const dutyAnalyticsHref = (range: DutyAnalyticsWindowParam, division?: string): string =>
  pageHref(DUTY_ANALYTICS_PATH, range, division);

/**
 * ⚠️ [P7C.1 §4] A DIVISION'S OWN PAGE, READ AS IT FROM THE COMMISSIONERATE: a path of its own, never a parameter
 * on the commissionerate page, so the two views are two routes and can never share a page instance. It keeps the
 * window, and the commissionerate page's own `?division=` narrowing as a web-only way back.
 */
export const divisionPageHref = (divisionId: string, range: DutyAnalyticsWindowParam, division?: string): string =>
  pageHref(`${DUTY_ANALYTICS_DIVISIONS_PATH}/${encodeURIComponent(divisionId)}`, range, division);

/**
 * What a drill URL names. [amended P7C.1] A preset `range` OR a custom pair — never both — and the division the
 * drill reads AS, only when it was opened from that division's page.
 */
export type DrillTarget = {
  readonly metric: DutyAnalyticsMetric;
  readonly cell: DrillCell;
  readonly asDivisionId?: string;
} & ({ readonly range: DutyAnalyticsRange } | CustomWindow);

/** The window a drill target names, as the builders take it. */
export const windowOfTarget = (target: DrillTarget): DutyAnalyticsWindowParam =>
  'range' in target ? target.range : { dateFrom: target.dateFrom, dateTo: target.dateTo };

const OBJECT_ID = /^[a-f0-9]{24}$/;

/** An id this page could have written: 24 lowercase hex characters, the server's own spelling of one. */
export const isObjectId = (value: string): boolean => OBJECT_ID.test(value);

/**
 * The page's `?division=` narrowing, carried for the way back only when it is an id the page itself could have
 * written — the drill page's rule since [P7C §2], moved here unchanged (case-insensitive) for its second user,
 * a division's page read from the commissionerate.
 */
export const wayBackDivisionOf = (division: string): string | undefined =>
  /^[0-9a-f]{24}$/i.test(division) ? division : undefined;

const isMetric = (value: string): value is DutyAnalyticsMetric =>
  (DUTY_ANALYTICS_METRICS as readonly string[]).includes(value);

const rangeOf = (value: string): DutyAnalyticsRange | null => {
  if (value === '') return DUTY_ANALYTICS_DEFAULT_RANGE;
  return DUTY_ANALYTICS_RANGES.find((range) => String(range) === value) ?? null;
};

type RawDrillParams = {
  readonly metric?: string;
  readonly divisionId?: string;
  readonly orgUnitId?: string;
  readonly asDivisionId?: string;
  readonly range?: string;
  readonly dateFrom?: string;
  readonly dateTo?: string;
};

/**
 * The window a drill URL names, or `null`. ⚠️ A PAIR FAILS CLOSED WITHOUT A `today` — the future-end rule cannot be
 * asked without one, and a pair nobody checked is a request the page could not have written. A pair beside a
 * `range`, half a pair, or a pair the shared rule refuses is `null` too.
 */
const drillWindowOf = (
  raw: RawDrillParams,
  today: string | undefined,
): { readonly range: DutyAnalyticsRange } | CustomWindow | null => {
  const dateFrom = raw.dateFrom ?? '';
  const dateTo = raw.dateTo ?? '';
  if (dateFrom === '' && dateTo === '') {
    const range = rangeOf(raw.range ?? '');
    return range === null ? null : { range };
  }
  if ((raw.range ?? '') !== '' || today === undefined) return null;
  return customWindowOf(dateFrom, dateTo, today);
};

/**
 * ⚠️ A DRILL URL THE PAGE COULD NOT HAVE WRITTEN IS A NOT-FOUND PAGE, NEVER A REQUEST — `DashboardTile`'s
 * rule: an unknown metric, a malformed id or a range outside the three answers here, without asking.
 * What the client cannot know — a unit id outside the caller's scope — the server answers with a 404.
 * [amended P7C.1] So does a custom pair `drillWindowOf` refuses, and `asDivisionId` beside a `divisionId`.
 * `today` is the IST calendar day (`istNow()`); without it a pair is refused rather than trusted.
 */
export const parseDrillParams = (raw: RawDrillParams, today?: string): DrillTarget | null => {
  const metric = raw.metric ?? '';
  const divisionId = raw.divisionId ?? '';
  const orgUnitId = raw.orgUnitId ?? '';
  const asDivisionId = raw.asDivisionId ?? '';
  if (!isMetric(metric)) return null;
  if ([divisionId, orgUnitId, asDivisionId].some((id) => id !== '' && !isObjectId(id))) return null;
  if (asDivisionId !== '' && divisionId !== '') return null;
  const chosen = drillWindowOf(raw, today);
  if (chosen === null) return null;
  return {
    metric,
    ...chosen,
    cell: {
      ...(divisionId === '' ? {} : { divisionId }),
      ...(orgUnitId === '' ? {} : { orgUnitId }),
    },
    ...(asDivisionId === '' ? {} : { asDivisionId }),
  };
};

/**
 * ⚠️ §2h — THE DRILL NEVER RENDERS A COLUMN THAT NAMES A PERSON, WHATEVER THE SERVER SENDS. Attendance
 * and markings drill to DUTIES with counts; the server's projection names nobody, and this is the
 * second half so a later projection change cannot put a name on screen through the drill page. A
 * COUNT of officers (`officers`) is not a person and is kept.
 */
const PERSON_COLUMN = /^(?:officer|officerId|officerName|officerNames|subjectOfficerId|phone)$/i;

export const visibleDrillColumns = (columns: readonly DashboardTileColumnDto[]): DashboardTileColumnDto[] =>
  columns.filter((column) => !PERSON_COLUMN.test(column.key));

// ─────────────────────────────────────────────────────────────────────────────────
//  THE DELEGATION BARS
// ─────────────────────────────────────────────────────────────────────────────────

export type DelegationBar = {
  readonly orgUnitId: string;
  readonly name: string;
  /** The true counts, for the sentence. */
  readonly asked: number;
  readonly handedDown: number;
  /** What the bar draws: handed down clamped to asked, the rest kept, and the gap to the longest bar. */
  readonly drawnHandedDown: number;
  readonly kept: number;
  readonly room: number;
  readonly max: number;
};

/**
 * ⚠️ FLEX-GROW SEGMENTS, SO NO PROPORTION IS EVER COMPUTED — the bar is three integer counts handed to
 * `flex-grow` over a zero basis, and the browser does the drawing. Every bar shares the largest asked
 * as its length, so bars compare.
 *
 * ⚠️ HANDED DOWN IS CLAMPED TO ASKED FOR THE DRAWING ONLY. The two come from different reads (the card's
 * delegation markers and the shares), and a bar whose segments ran past its own length would stop
 * being to scale; the sentence beside it keeps the true numbers. A row asked for nothing has no bar.
 */
export const delegationBars = (
  rows: readonly { orgUnitId: string; name: string; asked: number; handedDown: number }[],
): DelegationBar[] => {
  const drawn = rows.filter((row) => row.asked > 0);
  const max = Math.max(0, ...drawn.map((row) => row.asked));
  return drawn.map((row) => {
    const drawnHandedDown = Math.min(row.handedDown, row.asked);
    return { ...row, drawnHandedDown, kept: row.asked - drawnHandedDown, room: max - row.asked, max };
  });
};

// ─────────────────────────────────────────────────────────────────────────────────
//  WORDS THAT ARE DERIVED, NOT WRITTEN
// ─────────────────────────────────────────────────────────────────────────────────

const readable = (date: string): string => readableDate(date) ?? date;

/** The server's echoed window, never the browser's clock: "28 Sep 2026 – 4 Oct 2026", or one day. */
export const windowWords = (window: DutyAnalyticsWindowDto): string =>
  window.from === window.to ? readable(window.to) : `${readable(window.from)} – ${readable(window.to)}`;

/** A section's data, or `null` when the server said it could not compute it. */
export const sectionData = <T>(section: { data: T } | { failure: 'UNAVAILABLE' }): T | null =>
  'data' in section ? section.data : null;
